import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Stack,
  TextField,
  Chip,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import Autocomplete from "@mui/material/Autocomplete";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";

import { fetchData } from "../../../fetchData";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import {
  ReservationFormProps,
  ReservationFormErrors,
  User,
  Participant,
} from "../../../types/rooms";

const NewReserveForm = ({
  onClose,
  initialDate,
  onSuccess,
  editData,
  viewOnly = false,
}: ReservationFormProps) => {
  const snackbar = useSnackbar();
  const [room, setRoom] = useState(editData?.room || "Sala de reunião");
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [date, setDate] = useState<any>(
    editData
      ? dayjs(editData.startTime)
      : initialDate
      ? dayjs(initialDate)
      : dayjs(),
  );
  const [start, setStart] = useState(
    editData ? dayjs(editData.startTime).format("HH:mm") : "09:00",
  );
  const [end, setEnd] = useState(
    editData ? dayjs(editData.endTime).format("HH:mm") : "10:00",
  );
  const [reason, setReason] = useState(editData?.reason || "");
  const [isClient, setIsClient] = useState(editData?.isClient || false);
  const [progress, setProgress] = useState(false);
  const [errors, setErrors] = useState<ReservationFormErrors>({});

  const loadUsers = async () => {
    try {
      const result = await fetchData("/users?is_admin=true&status=true", {
        method: "GET",
      });

      if (result && result.rows) {
        setUsers(Array.isArray(result.rows) ? result.rows : []);
      } else {
        const fallbackResult = await fetchData("/runrunit/users", {
          method: "GET",
        });
        setUsers(Array.isArray(fallbackResult) ? fallbackResult : []);
      }
    } catch (e) {
      console.error("Erro ao carregar usuários:", e);
      try {
        const fallbackResult = await fetchData("/runrunit/users", {
          method: "GET",
        });
        setUsers(Array.isArray(fallbackResult) ? fallbackResult : []);
      } catch (fallbackError) {
        console.error("Erro no fallback também:", fallbackError);
        setUsers([]);
      }
    }
  };

  useEffect(() => {
    loadUsers();

    if (editData) {
      const participantNames = editData.participants || [];
      setParticipants(
        Array.isArray(participantNames)
          ? participantNames.map((name) => ({ name, id: name }))
          : [],
      );
    } else {
      const draft = sessionStorage.getItem("rooms:newReserveDraft");
      if (draft) {
        try {
          const parsed = JSON.parse(draft);
          setRoom(parsed.room || "Sala de reunião");
          setParticipants(
            Array.isArray(parsed.participants) ? parsed.participants : [],
          );
          setDate(parsed.date ? dayjs(parsed.date) : dayjs());
          setStart(parsed.start || "09:00");
          setEnd(parsed.end || "10:00");
          setReason(parsed.reason || "");
          setIsClient(parsed.isClient || false);
        } catch (e) {
          // ignore parse errors
        }
      }
    }
  }, [editData]);

  const persistDraft = () => {
    sessionStorage.setItem(
      "rooms:newReserveDraft",
      JSON.stringify({
        room,
        participants,
        date: date?.toISOString?.(),
        start,
        end,
        reason,
        isClient,
      }),
    );
  };

  const removeParticipant = (id: string) => {
    const updated = participants.filter((u) => u.id !== id);
    setParticipants(updated);
    sessionStorage.setItem(
      "rooms:newReserveDraft",
      JSON.stringify({
        room,
        participants: updated,
        date: date?.toISOString?.(),
        start,
        end,
        reason,
        isClient,
      }),
    );
  };

  const shouldDisableDate = (d: any) => {
    const jsDate = d?.toDate ? d.toDate() : new Date(d);
    const day = jsDate.getDay();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isPast = jsDate < today;
    return day === 0 || day === 6 || isPast;
  };

  const checkTimeConflict = async (
    startTime: string,
    endTime: string,
    date: any,
  ) => {
    try {
      const startDateTime = dayjs(date)
        .hour(parseInt(startTime.split(":")[0]))
        .minute(parseInt(startTime.split(":")[1]))
        .second(0)
        .millisecond(0)
        .toISOString();

      const endDateTime = dayjs(date)
        .hour(parseInt(endTime.split(":")[0]))
        .minute(parseInt(endTime.split(":")[1]))
        .second(0)
        .millisecond(0)
        .toISOString();

      const result = await fetchData(
        `/room-reservations/check-conflict?startTime=${startDateTime}&endTime=${endDateTime}&room=${room}${
          editData ? `&excludeId=${editData._id}` : ""
        }`,
        { method: "GET" },
      );

      return result?.conflict || null;
    } catch (error) {
      console.error("Erro ao verificar conflito:", error);
      return null;
    }
  };

  const validateDuration = (startTime: string, endTime: string) => {
    const start = dayjs(`2000-01-01 ${startTime}`);
    const end = dayjs(`2000-01-01 ${endTime}`);
    const duration = end.diff(start, "hour", true);

    if (duration > 2) {
      return `A duração da reserva não pode ser maior que 2 horas.`;
    }
    return null;
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    const newErrors: ReservationFormErrors = {};

    if (!participants.length) {
      newErrors.participants = "Selecione ao menos um participante";
    }
    if (!reason || reason.trim().length < 5)
      newErrors.reason = `Informe um motivo (min. 5 caracteres)`;

    const durationError = validateDuration(start, end);
    if (durationError) {
      newErrors.duration = durationError;
    }

    const conflictingReservation = await checkTimeConflict(start, end, date);
    if (conflictingReservation) {
      const conflictStart = dayjs(conflictingReservation.startTime).format(
        "HH:mm",
      );
      const conflictEnd = dayjs(conflictingReservation.endTime).format("HH:mm");
      newErrors.timeConflict = `Conflito de horário! Já existe uma reserva das ${conflictStart} às ${conflictEnd} neste dia.`;
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setProgress(true);

      try {
        const reservationData = {
          room,
          startTime: dayjs(date)
            .hour(parseInt(start.split(":")[0]))
            .minute(parseInt(start.split(":")[1]))
            .second(0)
            .millisecond(0)
            .toISOString(),
          endTime: dayjs(date)
            .hour(parseInt(end.split(":")[0]))
            .minute(parseInt(end.split(":")[1]))
            .second(0)
            .millisecond(0)
            .toISOString(),
          reason,
          participants: participants.map((p) =>
            typeof p === "string" ? p : p.name,
          ),
          isClient,
        };

        if (editData) {
          const originalParticipants = Array.isArray(editData.participants)
            ? [...editData.participants].sort()
            : [];
          const currentParticipants = [...reservationData.participants].sort();

          const participantsChanged =
            originalParticipants.length !== currentParticipants.length ||
            !originalParticipants.every((p, i) => p === currentParticipants[i]);

          const noChanges =
            editData.room === room &&
            dayjs(editData.startTime).toISOString() ===
              reservationData.startTime &&
            dayjs(editData.endTime).toISOString() === reservationData.endTime &&
            editData.reason === reason &&
            editData.isClient === isClient &&
            !participantsChanged;

          if (noChanges) {
            snackbar?.open("Nenhuma alteração para salvar.", "info");
            if (onClose) onClose();
            setProgress(false);
            return;
          }

          const updateData: any = {};

          if (editData.room !== room) updateData.room = room;
          if (
            dayjs(editData.startTime).toISOString() !==
            reservationData.startTime
          ) {
            updateData.startTime = reservationData.startTime;
          }
          if (
            dayjs(editData.endTime).toISOString() !== reservationData.endTime
          ) {
            updateData.endTime = reservationData.endTime;
          }
          if (editData.reason !== reason) updateData.reason = reason;
          if (editData.isClient !== isClient) updateData.isClient = isClient;
          if (participantsChanged)
            updateData.participants = reservationData.participants;

          await fetchData(`/room-reservations/${editData._id}`, {
            method: "PATCH",
            body: JSON.stringify(updateData),
          });
          snackbar?.open("Reserva atualizada com sucesso!", "success");
        } else {
          await fetchData("/room-reservations", {
            method: "POST",
            body: JSON.stringify(reservationData),
          });
          snackbar?.open("Reserva criada com sucesso!", "success");
        }

        sessionStorage.removeItem("rooms:newReserveDraft");
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } catch (error) {
        console.error("Erro ao salvar reserva:", error);
        snackbar?.open(
          "Ops! No momento não foi possível salvar a reserva.",
          "error",
        );
      } finally {
        setProgress(false);
      }
    } else {
      persistDraft();
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
      <Stack
        spacing={2}
        sx={{
          pb: 2,
          maxHeight: { xs: "60vh", sm: "70vh", md: "75vh" },
          overflow: "auto",
          pt: 1,
        }}
      >
        <TextField
          select
          fullWidth
          size="small"
          label="Sala"
          value={room}
          onChange={(e) => {
            setRoom(e.target.value);
            persistDraft();
          }}
          SelectProps={{ native: true }}
          disabled={viewOnly}
        >
          <option value="Sala de reunião">Sala de reunião</option>
        </TextField>

        <FormControlLabel
          control={
            <Checkbox
              checked={isClient}
              onChange={(e) => {
                setIsClient(e.target.checked);
                persistDraft();
              }}
              disabled={viewOnly}
            />
          }
          label="Reunião com cliente"
        />

        <Autocomplete
          options={[...(users || [])]
            .filter(
              (user) =>
                !participants.find((p) => p.id === (user._id || user.id)) &&
                ![
                  "Anderson Black",
                  "Anderson Nascimento",
                  "Bot Atlan",
                ].includes(user.name || ""),
            )
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""))}
          getOptionLabel={(option: User) => option.name || ""}
          value={null}
          onChange={(_, value) => {
            if (
              value &&
              !participants.find((u) => u.id === (value._id || value.id))
            ) {
              const updated = [
                ...participants,
                { ...value, id: value._id || value.id || "" },
              ];
              setParticipants(updated);
              setSearchInput("");
              sessionStorage.setItem(
                "rooms:newReserveDraft",
                JSON.stringify({
                  room,
                  participants: updated,
                  date: date?.toISOString?.(),
                  start,
                  end,
                  reason,
                  isClient,
                }),
              );
            }
          }}
          inputValue={searchInput}
          onInputChange={(_, newInputValue) => {
            setSearchInput(newInputValue);
          }}
          disabled={viewOnly}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Participantes"
              placeholder="Pesquisar e selecionar"
            />
          )}
        />
        {participants.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ mb: 0.5, display: "block" }}>
              Selecionados:
            </Typography>
            <Stack spacing={0.5}>
              {participants.map((participant) => (
                <Chip
                  key={participant.id}
                  label={participant.name}
                  onDelete={
                    viewOnly
                      ? undefined
                      : () => removeParticipant(participant.id)
                  }
                  size="small"
                />
              ))}
            </Stack>
          </Box>
        )}
        {errors.participants && (
          <Typography variant="caption" color="error">
            {errors.participants}
          </Typography>
        )}

        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
          <MobileDatePicker
            value={date}
            onChange={(v) => {
              if (v) setDate(v);
              persistDraft();
              if (errors.timeConflict) {
                setErrors({ ...errors, timeConflict: undefined });
              }
            }}
            shouldDisableDate={shouldDisableDate as any}
            disabled={viewOnly}
            slotProps={{
              textField: { size: "small", fullWidth: true, label: "Data" },
            }}
          />
        </LocalizationProvider>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField
            type="time"
            size="small"
            label="Início"
            value={start}
            onChange={(e) => {
              setStart(e.target.value);
              persistDraft();
              if (errors.timeConflict || errors.duration) {
                setErrors({
                  ...errors,
                  timeConflict: undefined,
                  duration: undefined,
                });
              }
            }}
            inputProps={{
              step: 30 * 60,
              min: "08:00",
              max: "17:30",
            }}
            fullWidth
            disabled={viewOnly}
          />
          <TextField
            type="time"
            size="small"
            label="Fim"
            value={end}
            onChange={(e) => {
              setEnd(e.target.value);
              persistDraft();
              if (errors.timeConflict || errors.duration) {
                setErrors({
                  ...errors,
                  timeConflict: undefined,
                  duration: undefined,
                });
              }
            }}
            inputProps={{
              step: 30 * 60,
              min: "08:00",
              max: "17:30",
            }}
            fullWidth
            disabled={viewOnly}
          />
        </Stack>

        {errors.timeConflict && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mt: 1, display: "block" }}
          >
            {errors.timeConflict}
          </Typography>
        )}

        {errors.duration && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mt: 1, display: "block" }}
          >
            {errors.duration}
          </Typography>
        )}

        <TextField
          label="Motivo"
          size="small"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (errors.reason) setErrors({ ...errors, reason: undefined });
            persistDraft();
          }}
          inputProps={{ maxLength: 200 }}
          multiline
          minRows={2}
          fullWidth
          error={Boolean(errors.reason)}
          helperText={errors.reason}
          disabled={viewOnly}
        />

        {viewOnly ? (
          <Button variant="contained" fullWidth onClick={onClose}>
            Fechar
          </Button>
        ) : (
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={progress}
          >
            {progress ? (
              <CircularProgress size={20} color="inherit" />
            ) : editData ? (
              "Atualizar Reserva"
            ) : (
              "Salvar"
            )}
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default NewReserveForm;
